import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay } from '@fortawesome/free-solid-svg-icons';
import webVulnScanService from '../../services/webVulnScanService';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import { toast } from 'react-toastify';
import './ScanConfig.css';

const ScanConfig = ({ webTargetId, projectId, onScanCreated }) => {
  const { t } = useLanguage();
  const { darkMode } = useTheme();
  const [config, setConfig] = useState({
    scanName: '',
    scanType: 'all',
    targetScope: {
      type: 'host',
      value: '',
      includeChildren: true
    },
    insertionPoints: ['url_params', 'body_params', 'headers'],
    speed: 'normal',
    timing: {
      requestsPerSecond: 3,
      randomJitter: true,
      jitterRange: 0.5
    },
    payloadFilter: {
      onlyEvasionTechniques: false,
      evasionTechniques: [],
      maxPayloadsPerType: 0
    }
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const scan = await webVulnScanService.createScan({
        webTargetId,
        projectId,
        ...config
      });

      // Start scan immediately
      await webVulnScanService.startScan(scan._id);

      toast.success(t('webTesting.scanStarted'));
      onScanCreated();

      // Reset form
      setConfig({
        scanName: '',
        scanType: 'all',
        targetScope: {
          type: 'host',
          value: '',
          includeChildren: true
        },
        insertionPoints: ['url_params', 'body_params', 'headers'],
        speed: 'normal'
      });
    } catch (error) {
      console.error('Failed to create scan:', error);
      toast.error(t('errors.scanCreationFailed'));
    }
  };

  const handleInsertionPointToggle = (point) => {
    const points = config.insertionPoints.includes(point)
      ? config.insertionPoints.filter(p => p !== point)
      : [...config.insertionPoints, point];
    setConfig({ ...config, insertionPoints: points });
  };

  // Dark mode styles
  const styles = {
    container: {
      background: darkMode ? '#1f2937' : '#ffffff',
      color: darkMode ? '#f3f4f6' : '#1f2937',
      padding: '24px',
      borderRadius: '8px'
    },
    input: {
      background: darkMode ? '#374151' : '#ffffff',
      color: darkMode ? '#f3f4f6' : '#1f2937',
      border: `1px solid ${darkMode ? '#4b5563' : '#d1d5db'}`
    },
    label: {
      color: darkMode ? '#d1d5db' : '#374151'
    },
    select: {
      background: darkMode ? '#374151' : '#ffffff',
      color: darkMode ? '#f3f4f6' : '#1f2937',
      border: `1px solid ${darkMode ? '#4b5563' : '#d1d5db'}`
    },
    heading: {
      color: darkMode ? '#f3f4f6' : '#1f2937',
      marginBottom: '20px'
    },
    form: {
      background: darkMode ? '#1f2937' : '#ffffff',
      padding: '24px',
      borderRadius: '8px',
      border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`
    }
  };

  return (
    <div className="scan-config" style={styles.container}>
      <h3 style={styles.heading}>{t('webTesting.newScanConfiguration')}</h3>

      <form onSubmit={handleSubmit} style={styles.form}>
        <div className="form-group">
          <label style={styles.label}>{t('common.name')}</label>
          <input
            type="text"
            value={config.scanName}
            onChange={(e) => setConfig({ ...config, scanName: e.target.value })}
            placeholder={t('webTesting.scanNamePlaceholder')}
            required
            style={styles.input}
          />
        </div>

        <div className="form-group">
          <label style={styles.label}>{t('webTesting.scanType')}</label>
          <select
            value={config.scanType}
            onChange={(e) => setConfig({ ...config, scanType: e.target.value })}
            style={styles.select}
          >
            <option value="all">{t('webTesting.scanAll')}</option>
            <option value="xss">{t('webTesting.scanXSS')}</option>
            <option value="sqli">{t('webTesting.scanSQLi')}</option>
            <option value="csrf">{t('webTesting.scanCSRF')}</option>
            <option value="ssrf">{t('webTesting.scanSSRF')}</option>
            <option value="rce">{t('webTesting.scanRCE')}</option>
            <option value="lfi">{t('webTesting.scanLFI')}</option>
            <option value="http_smuggling">{t('webTesting.scanHTTPSmuggling')}</option>
          </select>
        </div>

        <div className="form-group">
          <label style={styles.label}>{t('webTesting.targetScope')}</label>
          <select
            value={config.targetScope.type}
            onChange={(e) => setConfig({
              ...config,
              targetScope: { ...config.targetScope, type: e.target.value }
            })}
            style={styles.select}
          >
            <option value="host">{t('webTesting.entireHost')}</option>
            <option value="path">{t('webTesting.specificPath')}</option>
            <option value="url">{t('webTesting.singleURL')}</option>
          </select>
        </div>

        {(config.targetScope.type === 'path' || config.targetScope.type === 'url') && (
          <div className="form-group">
            <label style={styles.label}>{t('webTesting.targetValue')}</label>
            <input
              type="text"
              value={config.targetScope.value}
              onChange={(e) => setConfig({
                ...config,
                targetScope: { ...config.targetScope, value: e.target.value }
              })}
              placeholder="https://example.com/api"
              required
              style={styles.input}
            />
          </div>
        )}

        {config.targetScope.type === 'path' && (
          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={config.targetScope.includeChildren}
                onChange={(e) => setConfig({
                  ...config,
                  targetScope: { ...config.targetScope, includeChildren: e.target.checked }
                })}
              />
              {t('webTesting.includeChildPaths')}
            </label>
          </div>
        )}

        <div className="form-group">
          <label style={styles.label}>{t('webTesting.insertionPoints')}</label>
          <div className="checkbox-list">
            <label>
              <input
                type="checkbox"
                checked={config.insertionPoints.includes('url_params')}
                onChange={() => handleInsertionPointToggle('url_params')}
              />
              {t('webTesting.urlParameters')}
            </label>
            <label>
              <input
                type="checkbox"
                checked={config.insertionPoints.includes('body_params')}
                onChange={() => handleInsertionPointToggle('body_params')}
              />
              {t('webTesting.bodyParameters')}
            </label>
            <label>
              <input
                type="checkbox"
                checked={config.insertionPoints.includes('headers')}
                onChange={() => handleInsertionPointToggle('headers')}
              />
              {t('webTesting.headers')}
            </label>
            <label>
              <input
                type="checkbox"
                checked={config.insertionPoints.includes('cookies')}
                onChange={() => handleInsertionPointToggle('cookies')}
              />
              {t('webTesting.cookies')}
            </label>
          </div>
        </div>

        <div className="form-group">
          <label style={styles.label}>{t('webTesting.scanSpeed')}</label>
          <select
            value={config.speed}
            onChange={(e) => setConfig({ ...config, speed: e.target.value })}
            style={styles.select}
          >
            <option value="fast">{t('webTesting.speedFast')}</option>
            <option value="normal">{t('webTesting.speedNormal')}</option>
            <option value="thorough">{t('webTesting.speedThorough')}</option>
          </select>
        </div>

        <div className="form-group">
          <label style={styles.label}>{t('webTesting.timingProfile')}</label>
          <select
            style={styles.select}
            onChange={(e) => {
              const presets = {
                aggressive: { requestsPerSecond: 10, randomJitter: false, jitterRange: 0 },
                fast: { requestsPerSecond: 5, randomJitter: true, jitterRange: 0.3 },
                normal: { requestsPerSecond: 3, randomJitter: true, jitterRange: 0.5 },
                slow: { requestsPerSecond: 1, randomJitter: true, jitterRange: 0.7 },
                stealth: { requestsPerSecond: 0.2, randomJitter: true, jitterRange: 1.0 }
              };
              if (e.target.value) {
                setConfig({ ...config, timing: presets[e.target.value] });
              }
            }}
          >
            <option value="">{t('webTesting.customTiming')}</option>
            <option value="aggressive">{t('webTesting.timingAggressive')} (10 req/s)</option>
            <option value="fast">{t('webTesting.timingFast')} (5 req/s)</option>
            <option value="normal">{t('webTesting.timingNormal')} (3 req/s)</option>
            <option value="slow">{t('webTesting.timingSlow')} (1 req/s)</option>
            <option value="stealth">{t('webTesting.timingStealth')} (0.2 req/s - evades SIEM)</option>
          </select>
          <small style={{fontSize: '12px', color: '#6b7280', marginTop: '4px', display: 'block'}}>
            {t('webTesting.timingHelp')}
          </small>
        </div>

        <div className="form-group">
          <label style={styles.label}>{t('webTesting.payloadFiltering')}</label>
          <div className="checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={config.payloadFilter.onlyEvasionTechniques}
                onChange={(e) => setConfig({
                  ...config,
                  payloadFilter: {
                    ...config.payloadFilter,
                    onlyEvasionTechniques: e.target.checked
                  }
                })}
              />
              {t('webTesting.onlyWAFBypassPayloads')}
            </label>
          </div>
          {config.payloadFilter.onlyEvasionTechniques && (
            <div className="evasion-techniques" style={{ marginTop: '10px', marginLeft: '20px' }}>
              <small style={styles.label}>{t('webTesting.selectEvasionTechniques')}:</small>
              <div className="checkbox-list">
                {['case_variation', 'encoding', 'comment_obfuscation', 'whitespace_variation', 'operator_variation'].map(tech => (
                  <label key={tech}>
                    <input
                      type="checkbox"
                      checked={config.payloadFilter.evasionTechniques.includes(tech)}
                      onChange={(e) => {
                        const techniques = e.target.checked
                          ? [...config.payloadFilter.evasionTechniques, tech]
                          : config.payloadFilter.evasionTechniques.filter(t => t !== tech);
                        setConfig({
                          ...config,
                          payloadFilter: {
                            ...config.payloadFilter,
                            evasionTechniques: techniques
                          }
                        });
                      }}
                    />
                    {tech.replace(/_/g, ' ')}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        <button type="submit" className="btn-primary btn-start-scan">
          <FontAwesomeIcon icon={faPlay} />
          {t('webTesting.startScan')}
        </button>
      </form>
    </div>
  );
};

export default ScanConfig;
